import { useEffect, useRef, useState } from "react";
import { X, Share, DownloadSimple, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const KG_TO_LB = 2.20462;

// Canvas is rendered at 2x for retina-crisp downloads
const W = 1080;
const H = 1350; // Instagram Story-friendly ratio (4:5)

/**
 * Draw a shareable win-card to canvas using brand colors + the SINCE-STARTING delta.
 * Returns the canvas element ready for toBlob() / toDataURL() export.
 */
function drawWinCard({ canvas, brand, weight, bodyFat, durationDays, unit, clientName, anonymise }) {
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;
  const accent = brand?.accent || "var(--brand)";
  const bgGradient = ctx.createLinearGradient(0, 0, W, H);
  bgGradient.addColorStop(0, "#0A0A0A");
  bgGradient.addColorStop(1, "#171717");
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, W, H);

  // Accent rule (top)
  ctx.fillStyle = accent;
  ctx.fillRect(80, 80, 80, 6);

  // Brand name top-left
  ctx.fillStyle = accent;
  ctx.font = "900 26px Helvetica, Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.letterSpacing = "4px";
  ctx.fillText((brand?.name || "TRAINA").toUpperCase(), 80, 130);

  // Headline
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 96px Helvetica, Arial, sans-serif";
  ctx.fillText("PROGRESS.", 80, 260);

  // Subhead
  ctx.fillStyle = "#A1A1AA";
  ctx.font = "400 28px Helvetica, Arial, sans-serif";
  ctx.fillText(anonymise ? "Tracked across" : `${(clientName || "Client").toUpperCase()} · TRACKED ACROSS`, 80, 310);
  ctx.fillStyle = accent;
  ctx.font = "700 28px Helvetica, Arial, sans-serif";
  ctx.fillText(`${durationDays} DAY${durationDays === 1 ? "" : "S"}`, 80, 350);

  // Stat blocks
  let y = 480;
  const block = (label, baseline, current, change, suffix, isGood) => {
    // Label
    ctx.fillStyle = "#71717A";
    ctx.font = "700 18px Helvetica, Arial, sans-serif";
    ctx.fillText(label.toUpperCase(), 80, y);
    // Baseline → current
    ctx.fillStyle = "#52525B";
    ctx.font = "300 36px Helvetica, Arial, sans-serif";
    ctx.fillText(baseline, 80, y + 60);
    // Arrow
    ctx.fillStyle = accent;
    ctx.font = "700 36px Helvetica, Arial, sans-serif";
    ctx.fillText("→", 80 + ctx.measureText(baseline).width + 24, y + 60);
    // Current (large)
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "900 84px Helvetica, Arial, sans-serif";
    const arrowWidth = ctx.measureText("→").width;
    ctx.font = "300 36px Helvetica, Arial, sans-serif";
    const baseW = ctx.measureText(baseline).width;
    const curX = 80 + baseW + 24 + arrowWidth + 24;
    ctx.font = "900 84px Helvetica, Arial, sans-serif";
    ctx.fillText(current, curX, y + 68);
    // Delta pill
    ctx.fillStyle = isGood ? "#10B981" : "#F59E0B";
    ctx.font = "900 32px Helvetica, Arial, sans-serif";
    ctx.fillText(change, 80, y + 130);
    y += 220;
  };

  if (weight && weight.points >= 2) {
    const baseDisplay = unit === "lb" ? `${(weight.baseline * KG_TO_LB).toFixed(1)} lb` : `${weight.baseline.toFixed(1)} kg`;
    const curDisplay = unit === "lb" ? `${(weight.current * KG_TO_LB).toFixed(1)} lb` : `${weight.current.toFixed(1)} kg`;
    const ch = (unit === "lb" ? weight.change * KG_TO_LB : weight.change) ?? 0;
    const sign = ch > 0 ? "+" : "";
    const chDisplay = `${sign}${ch.toFixed(1)} ${unit} (${sign}${weight.change_pct?.toFixed(1) ?? "0.0"}%)`;
    block("WEIGHT", baseDisplay, curDisplay, chDisplay, unit, ch <= 0);
  }
  if (bodyFat && bodyFat.points >= 2) {
    const sign = bodyFat.change > 0 ? "+" : "";
    block(
      "BODY FAT",
      `${bodyFat.baseline.toFixed(1)}%`,
      `${bodyFat.current.toFixed(1)}%`,
      `${sign}${bodyFat.change.toFixed(1)} pp`,
      "%",
      bodyFat.change <= 0,
    );
  }

  // Footer
  ctx.fillStyle = accent;
  ctx.fillRect(80, H - 130, 60, 4);
  ctx.fillStyle = "#71717A";
  ctx.font = "700 18px Helvetica, Arial, sans-serif";
  ctx.fillText(`POWERED BY ${(brand?.name || "TRAINA").toUpperCase()}`, 80, H - 90);
}

export default function WinCardShare({ timeline, brand, unit = "kg", clientName, onClose }) {
  const canvasRef = useRef(null);
  const [anonymise, setAnonymise] = useState(false);
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!canvasRef.current || !timeline) return;
    drawWinCard({
      canvas: canvasRef.current,
      brand,
      weight: timeline.weight,
      bodyFat: timeline.body_fat,
      durationDays: timeline.duration_days || 0,
      unit,
      clientName,
      anonymise,
    });
    setPreviewUrl(canvasRef.current.toDataURL("image/png"));
  }, [timeline, brand, unit, clientName, anonymise]);

  const download = () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `progress-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Win-card saved to downloads");
    }, "image/png");
  };

  const copyImage = async () => {
    if (!canvasRef.current) return;
    try {
      canvasRef.current.toBlob(async (blob) => {
        if (!blob) return;
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          setCopied(true);
          toast.success("Image copied to clipboard");
          setTimeout(() => setCopied(false), 2000);
        } catch (_e) {
          toast.error("Clipboard image-write not supported in this browser — use Download instead");
        }
      }, "image/png");
    } catch (_e) {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="win-card-modal"
    >
      <div
        className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-5 border-b border-zinc-800 flex items-center justify-between gap-4 sticky top-0 bg-zinc-950 z-10">
          <div>
            <p className="tracker text-zinc-500 text-[10px]">[ SHARE ]</p>
            <h2 className="font-display text-2xl">Win card.</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-white"
            data-testid="win-card-close"
          >
            <X size={20} weight="bold" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Preview */}
          {previewUrl && (
            <div className="bg-black border border-zinc-800 p-2 flex items-center justify-center" data-testid="win-card-preview-wrap">
              <img
                src={previewUrl}
                alt="Win card preview"
                className="max-h-[60vh] w-auto"
                data-testid="win-card-preview"
              />
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* Controls */}
          <label className="flex items-center gap-2 text-zinc-300 text-sm cursor-pointer" data-testid="win-card-anonymise">
            <input
              type="checkbox"
              checked={anonymise}
              onChange={(e) => setAnonymise(e.target.checked)}
              className="accent-emerald-500"
              data-testid="win-card-anonymise-input"
            />
            Hide my name (anonymise)
          </label>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={download}
              className="btn-primary !py-3 !text-xs justify-center"
              data-testid="win-card-download"
            >
              <DownloadSimple size={14} weight="bold" /> Download PNG
            </button>
            <button
              type="button"
              onClick={copyImage}
              className="btn-ghost !py-3 !text-xs justify-center"
              data-testid="win-card-copy"
            >
              {copied ? (
                <>
                  <CheckCircle size={14} weight="fill" /> Copied!
                </>
              ) : (
                <>
                  <Copy size={14} weight="bold" /> Copy image
                </>
              )}
            </button>
          </div>
          <p className="text-zinc-500 text-[10px] tracker text-center pt-1">
            1080 × 1350 · Instagram Stories-ready
          </p>
        </div>
      </div>
    </div>
  );
}
